import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import DashboardLayout from "./DashboardLayout/DasboardLayout";
import { API_URL } from "./config";
import { Timer, AlertTriangle, CheckCircle, Filter, Clock, Car, Users } from "lucide-react";

const WaitTimeMonitor = () => {
  const [privateTrips, setPrivateTrips] = useState([]);
  const [rideshareTrips, setRideshareTrips] = useState([]);
  const [rideshareRequests, setRideshareRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("private"); // private or rideshare
  const APILINK = API_URL;

  useEffect(() => {
    fetchWaitTimeData();
    const interval = setInterval(fetchWaitTimeData, 5000);
    return () => clearInterval(interval);
  }, [fetchWaitTimeData]);

  const fetchWaitTimeData = async () => {
    try {
      setLoading(true);
      
      const [privateResponse, rideshareResponse, requestsResponse] = await Promise.all([
        fetch(`${APILINK}/trip/`),
        fetch(`${APILINK}/rideshare/`),
        fetch(`${APILINK}/rideshare/requests/all`)
      ]);

      const [privateData, rideshareData, requestsData] = await Promise.all([
        privateResponse.json(),
        rideshareResponse.json(),
        requestsResponse.json()
      ]);

      console.log("WaitTimeMonitor - Private trips:", privateData?.length || 0);
      console.log("WaitTimeMonitor - Rideshare trips:", rideshareData?.length || 0);
      console.log("WaitTimeMonitor - Rideshare requests:", requestsData?.length || 0);
      
      // Debug data types
      console.log("WaitTimeMonitor - privateTrips type:", typeof privateTrips, Array.isArray(privateTrips) ? "array" : "not array");
      console.log("WaitTimeMonitor - rideshareTrips type:", typeof rideshareTrips, Array.isArray(rideshareTrips) ? "array" : "not array");
      console.log("WaitTimeMonitor - rideshareRequests type:", typeof rideshareRequests, Array.isArray(rideshareRequests) ? "array" : "not array");
      
      // Debug sample data
      if (privateData && privateData.length > 0) {
        console.log("WaitTimeMonitor - Sample private trip:", privateData[0]);
      }
      if (rideshareData && rideshareData.length > 0) {
        console.log("WaitTimeMonitor - Sample rideshare trip:", rideshareData[0]);
      }
      if (requestsData && requestsData.length > 0) {
        console.log("WaitTimeMonitor - Sample rideshare request:", requestsData[0]);
      }

      setPrivateTrips(privateData || []);
      setRideshareTrips(rideshareData || []);
      setRideshareRequests(requestsData || []);
    } catch (error) {
      console.error("Error fetching wait time data:", error);
    } finally {
      setLoading(false);
    }
  };

  const analyzePrivateTripWaitTime = (trip) => {
    const analysis = {
      trip_id: trip.trip_id,
      status: trip.status,
      request_start_datetime: trip.request_start_datetime,
      driver_arrived_at_pickup_datetime: trip.driver_arrived_at_pickup_datetime,
      waiting_time_minutes: null,
      valid: true,
      issues: [],
      requested_date: null,
      arrived_date: null
    };

    try {
      // Parse dates - database stores in UTC, frontend needs to convert to local time
      if (trip.request_start_datetime && trip.request_start_datetime !== '0000-00-00 00:00:00') {
        // Convert UTC database time to local time (Africa/Harare UTC+2)
        const utcRequested = new Date(trip.request_start_datetime);
        if (!isNaN(utcRequested.getTime())) {
          // Add 2 hours for Africa/Harare timezone
          analysis.requested_date = new Date(utcRequested.getTime() + (2 * 60 * 60 * 1000));
        }
      }

      if (trip.driver_arrived_at_pickup_datetime && trip.driver_arrived_at_pickup_datetime !== '0000-00-00 00:00:00') {
        // Convert UTC database time to local time (Africa/Harare UTC+2)
        const utcArrived = new Date(trip.driver_arrived_at_pickup_datetime);
        if (!isNaN(utcArrived.getTime())) {
          // Add 2 hours for Africa/Harare timezone
          analysis.arrived_date = new Date(utcArrived.getTime() + (2 * 60 * 60 * 1000));
        }
      }

      // Calculate wait time in minutes
      if (analysis.requested_date && analysis.arrived_date) {
        const waitingTimeMinutes = (analysis.arrived_date - analysis.requested_date) / (1000 * 60);
        
        // Validate wait time
        if (waitingTimeMinutes < 0) {
          analysis.valid = false;
          analysis.issues.push("Negative wait time - arrival before request");
          analysis.waiting_time_minutes = waitingTimeMinutes;
        } else if (waitingTimeMinutes > 1440) { // More than 24 hours
          analysis.valid = false;
          analysis.issues.push("Extreme wait time - likely invalid data");
          analysis.waiting_time_minutes = waitingTimeMinutes;
        } else {
          analysis.waiting_time_minutes = waitingTimeMinutes;
        }
      } else {
        analysis.valid = false;
        if (!analysis.requested_date) analysis.issues.push("Missing or invalid request time");
        if (!analysis.arrived_date) analysis.issues.push("Missing or invalid arrival time");
      }

    } catch (error) {
      analysis.valid = false;
      analysis.issues.push(`Date parsing error: ${error.message}`);
    }

    return analysis;
  };

  const analyzeRideshareWaitTime = (trip, request) => {
    const analysis = {
      rideshare_id: trip.rideshare_id,
      request_id: request.request_id,
      status: trip.status,
      request_status: request.status,
      created_at: trip.created_at,
      updated_at: request.updated_at,
      driver_arrived_at_pickup_datetime: trip.driver_arrived_at_pickup_datetime || request.driver_arrived_at_pickup_datetime,
      valid: true,
      issues: []
    };

    if (!analysis.driver_arrived_at_pickup_datetime) {
      analysis.issues.push("Missing driver_arrived_at_pickup_datetime");
      return analysis;
    }

    if (analysis.driver_arrived_at_pickup_datetime === "0000-00-00 00:00:00") {
      analysis.issues.push("Driver arrival timestamp is default value");
      return analysis;
    }

    try {
      // Convert UTC database times to local time (Africa/Harare UTC+2)
      const utcTripCreated = new Date(trip.created_at);
      const utcRequestJoined = new Date(request.updated_at);
      const utcArrived = new Date(analysis.driver_arrived_at_pickup_datetime);

      if (isNaN(utcTripCreated.getTime())) {
        analysis.valid = false;
        analysis.issues.push("Invalid trip created_at format");
        return analysis;
      }

      if (isNaN(utcRequestJoined.getTime())) {
        analysis.valid = false;
        analysis.issues.push("Invalid request updated_at format");
        return analysis;
      }

      if (isNaN(utcArrived.getTime())) {
        analysis.valid = false;
        analysis.issues.push("Invalid driver_arrived_at_pickup_datetime format");
        return analysis;
      }

      // Add 2 hours for Africa/Harare timezone conversion
      const tripCreated = new Date(utcTripCreated.getTime() + (2 * 60 * 60 * 1000));
      const requestJoined = new Date(utcRequestJoined.getTime() + (2 * 60 * 60 * 1000));
      const arrived = new Date(utcArrived.getTime() + (2 * 60 * 60 * 1000));

      const dispatchTimeMinutes = (arrived - tripCreated) / (1000 * 60);
      const passengerWaitTimeMinutes = (arrived - requestJoined) / (1000 * 60);

      analysis.dispatch_time_minutes = dispatchTimeMinutes;
      analysis.passenger_wait_time_minutes = passengerWaitTimeMinutes;
      analysis.trip_created_date = tripCreated;
      analysis.request_joined_date = requestJoined;
      analysis.arrived_date = arrived;

      if (dispatchTimeMinutes < 0) {
        analysis.valid = false;
        analysis.issues.push(`Negative dispatch time: ${dispatchTimeMinutes.toFixed(2)} minutes`);
      }

      if (dispatchTimeMinutes > 1440) {
        analysis.valid = false;
        analysis.issues.push(`Extreme dispatch time: ${dispatchTimeMinutes.toFixed(2)} minutes`);
      }

      if (passengerWaitTimeMinutes < 0) {
        analysis.valid = false;
        analysis.issues.push(`Negative passenger wait time: ${passengerWaitTimeMinutes.toFixed(2)} minutes`);
      }

      if (passengerWaitTimeMinutes > 1440) {
        analysis.valid = false;
        analysis.issues.push(`Extreme passenger wait time: ${passengerWaitTimeMinutes.toFixed(2)} minutes`);
      }

    } catch (error) {
      analysis.valid = false;
      analysis.issues.push(`Date parsing error: ${error.message}`);
    }

    return analysis;
  };

  const privateTripAnalyses = Array.isArray(privateTrips) ? privateTrips.map(analyzePrivateTripWaitTime) : [];
  const rideshareAnalyses = Array.isArray(rideshareTrips) && Array.isArray(rideshareRequests) ? rideshareTrips.map(trip => {
    const request = rideshareRequests.find(req => req.rideshare_id === trip.rideshare_id);
    return request ? analyzeRideshareWaitTime(trip, request) : null;
  }).filter(Boolean) : [];

  const getFilteredData = (analyses) => {
    switch (filter) {
      case "valid":
        return analyses.filter(a => a.valid);
      case "invalid":
        return analyses.filter(a => !a.valid);
      case "negative":
        return analyses.filter(a => a.issues.some(issue => issue.includes("Negative")));
      default:
        return analyses;
    }
  };

  const filteredPrivateTrips = getFilteredData(privateTripAnalyses);
  const filteredRideshareTrips = getFilteredData(rideshareAnalyses);

  const privateStats = {
    total: privateTripAnalyses.length,
    valid: privateTripAnalyses.filter(a => a.valid).length,
    invalid: privateTripAnalyses.filter(a => !a.valid).length,
    avgWaitTime: privateTripAnalyses.filter(a => a.valid && a.waiting_time_minutes)
      .reduce((sum, a) => sum + a.waiting_time_minutes, 0) / 
      privateTripAnalyses.filter(a => a.valid && a.waiting_time_minutes).length || 0
  };

  const rideshareStats = {
    total: rideshareAnalyses.length,
    valid: rideshareAnalyses.filter(a => a.valid).length,
    invalid: rideshareAnalyses.filter(a => !a.valid).length,
    avgDispatchTime: rideshareAnalyses.filter(a => a.valid && a.dispatch_time_minutes)
      .reduce((sum, a) => sum + a.dispatch_time_minutes, 0) / 
      rideshareAnalyses.filter(a => a.valid && a.dispatch_time_minutes).length || 0,
    avgPassengerWaitTime: rideshareAnalyses.filter(a => a.valid && a.passenger_wait_time_minutes)
      .reduce((sum, a) => sum + a.passenger_wait_time_minutes, 0) / 
      rideshareAnalyses.filter(a => a.valid && a.passenger_wait_time_minutes).length || 0
  };

  const filterOptions = [
    { value: "all", label: "All Data" },
    { value: "valid", label: "Valid Only" },
    { value: "invalid", label: "Invalid Only" },
    { value: "negative", label: "Negative Times" }
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 bg-white dark:bg-gray-800 min-h-screen">
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-6 text-gray-800 dark:text-white">
              Wait Time Monitor
            </h1>
            <div className="flex justify-center items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <span className="ml-4 text-gray-600 dark:text-gray-300">Loading wait time data...</span>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 bg-white dark:bg-gray-800 min-h-screen">
        <h1 className="text-3xl font-bold mb-6 text-gray-800 dark:text-white">
          Wait Time Monitor - Critical Analysis
        </h1>
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          Monitoring wait time calculations and data quality for both private rides and rideshare trips
        </p>

        {/* Statistics Cards */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Private Trips</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{privateStats.total}</p>
                <div className="flex gap-2 mt-2">
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">{privateStats.valid} valid</span>
                  <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">{privateStats.invalid} invalid</span>
                </div>
              </div>
              <Car className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg Private Wait</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{privateStats.avgWaitTime.toFixed(1)} min</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">From valid trips only</p>
              </div>
              <Clock className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Rideshare Trips</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{rideshareStats.total}</p>
                <div className="flex gap-2 mt-2">
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">{rideshareStats.valid} valid</span>
                  <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">{rideshareStats.invalid} invalid</span>
                </div>
              </div>
              <Users className="w-8 h-8 text-purple-600" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg Rideshare Wait</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{rideshareStats.avgPassengerWaitTime.toFixed(1)} min</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Passenger waiting time</p>
              </div>
              <Timer className="w-8 h-8 text-orange-600" />
            </div>
          </div>
        </div>

        {/* Filter Controls */}
        <div className="mb-6 bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-blue-800 dark:text-blue-200">Filter Data</span>
            </div>
            <div className="flex gap-2">
              {filterOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setFilter(option.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filter === option.value
                      ? "bg-blue-600 text-white"
                      : "bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-800"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab("private")}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "private"
                  ? "border-blue-500 text-blue-600 dark:text-blue-400"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200"
              }`}
            >
              Private Trips ({filteredPrivateTrips.length})
            </button>
            <button
              onClick={() => setActiveTab("rideshare")}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "rideshare"
                  ? "border-blue-500 text-blue-600 dark:text-blue-400"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200"
              }`}
            >
              Rideshare Trips ({filteredRideshareTrips.length})
            </button>
          </nav>
        </div>

        {/* Private Trips Table */}
        {activeTab === "private" && (
          <div className="overflow-x-auto shadow-md rounded-lg">
            <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
              <thead className="text-xs text-gray-700 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-300">
                <tr>
                  <th className="px-6 py-3">Trip ID</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Wait Time</th>
                  <th className="px-6 py-3">Request Time</th>
                  <th className="px-6 py-3">Arrival Time</th>
                  <th className="px-6 py-3">Issues</th>
                </tr>
              </thead>
              <tbody>
                {filteredPrivateTrips.map((analysis) => (
                  <tr
                    key={analysis.trip_id}
                    className={`bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 ${
                      !analysis.valid ? "bg-red-50 dark:bg-red-900" : ""
                    }`}
                  >
                    <td className="px-6 py-4 font-medium">{analysis.trip_id}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        analysis.valid 
                          ? "bg-green-100 text-green-800" 
                          : "bg-red-100 text-red-800"
                      }`}>
                        {analysis.valid ? "Valid" : "Invalid"}
                      </span>
                      <span className="ml-2 text-xs text-gray-500">{analysis.status}</span>
                    </td>
                    <td className="px-6 py-4">
                      {analysis.waiting_time_minutes ? (
                        <span className="font-mono font-bold text-blue-600">
                          {analysis.waiting_time_minutes.toFixed(2)} min
                        </span>
                      ) : (
                        "N/A"
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-xs">
                        <div className="font-mono">{analysis.request_start_datetime}</div>
                        {analysis.requested_date && (
                          <div className="text-gray-500">
                            {format(analysis.requested_date, "MMM dd, yyyy HH:mm")} (Local)
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-xs">
                        <div className="font-mono">{analysis.driver_arrived_at_pickup_datetime}</div>
                        {analysis.arrived_date && (
                          <div className="text-gray-500">
                            {format(analysis.arrived_date, "MMM dd, yyyy HH:mm")} (Local)
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {analysis.issues.length > 0 ? (
                        <div className="space-y-1">
                          {analysis.issues.map((issue, idx) => (
                            <div key={idx} className="text-xs text-red-600 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              {issue}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-xs text-green-600 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          No issues
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Rideshare Trips Table */}
        {activeTab === "rideshare" && (
          <div className="overflow-x-auto shadow-md rounded-lg">
            <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
              <thead className="text-xs text-gray-700 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-300">
                <tr>
                  <th className="px-6 py-3">Rideshare ID</th>
                  <th className="px-6 py-3">Request ID</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Dispatch Time</th>
                  <th className="px-6 py-3">Passenger Wait</th>
                  <th className="px-6 py-3">Trip Created</th>
                  <th className="px-6 py-3">Request Joined</th>
                  <th className="px-6 py-3">Issues</th>
                </tr>
              </thead>
              <tbody>
                {filteredRideshareTrips.map((analysis) => (
                  <tr
                    key={analysis.rideshare_id}
                    className={`bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 ${
                      !analysis.valid ? "bg-red-50 dark:bg-red-900" : ""
                    }`}
                  >
                    <td className="px-6 py-4 font-medium">{analysis.rideshare_id}</td>
                    <td className="px-6 py-4">
                      <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                        {analysis.request_id}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        analysis.valid 
                          ? "bg-green-100 text-green-800" 
                          : "bg-red-100 text-red-800"
                      }`}>
                        {analysis.valid ? "Valid" : "Invalid"}
                      </span>
                      <span className="ml-2 text-xs text-gray-500">{analysis.status}</span>
                    </td>
                    <td className="px-6 py-4">
                      {analysis.dispatch_time_minutes ? (
                        <div>
                          <div className="font-mono font-bold text-blue-600">
                            {analysis.dispatch_time_minutes.toFixed(2)} min
                          </div>
                          <div className="text-xs text-gray-500">Dispatch</div>
                        </div>
                      ) : (
                        "N/A"
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {analysis.passenger_wait_time_minutes ? (
                        <div>
                          <div className="font-mono font-bold text-green-600">
                            {analysis.passenger_wait_time_minutes.toFixed(2)} min
                          </div>
                          <div className="text-xs text-gray-500">Passenger</div>
                        </div>
                      ) : (
                        "N/A"
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-xs">
                        <div className="font-mono">{analysis.created_at}</div>
                        {analysis.trip_created_date && (
                          <div className="text-gray-500">
                            {format(analysis.trip_created_date, "MMM dd, yyyy HH:mm")} (Local)
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-xs">
                        <div className="font-mono">{analysis.updated_at}</div>
                        {analysis.request_joined_date && (
                          <div className="text-gray-500">
                            {format(analysis.request_joined_date, "MMM dd, yyyy HH:mm")} (Local)
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {analysis.issues.length > 0 ? (
                        <div className="space-y-1">
                          {analysis.issues.map((issue, idx) => (
                            <div key={idx} className="text-xs text-red-600 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              {issue}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-xs text-green-600 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          No issues
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Empty State */}
        {((activeTab === "private" && filteredPrivateTrips.length === 0) ||
          (activeTab === "rideshare" && filteredRideshareTrips.length === 0)) && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <div className="mb-4">
              <Filter className="w-12 h-12 mx-auto text-gray-300" />
            </div>
            <h3 className="text-lg font-medium mb-2">No trips found</h3>
            <p>No {activeTab} trips found matching the current filter criteria.</p>
          </div>
        )}

        {/* Refresh Button */}
        <div className="text-center mt-6">
          <button
            onClick={fetchWaitTimeData}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Timer className="w-4 h-4 mr-2" />
            Refresh Data
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default WaitTimeMonitor;
